import gql from "graphql-tag";

export const GET_BOOKINS = gql`
    query GetBookins($options: ArrayDataOptions, $onlyCurrentUser: Boolean) {
        bookings(options: $options, onlyCurrentUser: $onlyCurrentUser) {
            existsMore
            total
            items {
                id
                bookingNumber
                bookingNumberPrefix
                bookingType
                updateDate
                createDate
                startDateTime
                courseId
                noticeStaff
                attendanceListNote
                noticeBooker
                coursePriceId
                payDate
                grossPrice
                netPrice
                vat
                pricingReason
                isSpecial
                coursePrice {
                    id
                    grossPrice
                    netPrice
                    vat
                }
                invoice {
                    id
                    billed
                    invoiceNumber
                    invoiceNumberPrefix
                    createDate
                }
                reversalInvoice {
                    id
                    invoiceNumber
                    invoiceNumberPrefix
                    createDate
                }
                paymentType
                courseLessons {
                    id
                    startDateTime
                    endDateTime
                    locationId
                    indexOfLessonInCourse
                    course {
                        id
                        freePlaces
                        startDateTime
                        paymentInterval
                        minAttendees
                        maxAttendees
                        prefixedCourseNumber
                        allowMonthlyPayment
                        paymentInterval
                        courseType {
                            id
                            name
                            color
                        }
                        instructors {
                            id
                            firstname
                            lastname
                        }
                        lessonCount
                        allowLessonBooking
                    }
                    location {
                        id
                        city
                        name
                    }
                }
                attendee {
                    id
                    erasedAt
                    gender
                    firstname
                    lastname
                    birthday
                    customerNumberPrefix
                    customerNumber
                    member

                    booker {
                        id
                        gender
                        firstname
                        lastname
                        customerNumberPrefix
                        customerNumber
                        street
                        houseNumber
                        postCode
                        city
                        phoneNumber
                        mobileNumber
                        email
                        gender
                        nonDisclosureNotice
                        credit
                    }

                    badgeXAttendee {
                        id
                        createDate
                        dateOfAchivement
                        badge {
                            id
                            name
                            abbreviation
                            color
                        }
                    }
                }
                bookedBy
                canceled
                emailNotifications {
                    id
                    templateType
                    subject
                    createDate
                    bookingId
                    email
                }
                bookingConfirmationSent
                waitingListConfirmationSent
                cancelationSent
                invoiceSent
                paymentMethod {
                    id
                    type
                    cash
                    esr
                    iban
                    bank
                    bic
                    sepaAccountholder
                    bankTransfer
                    onlinePayment
                }
                waitListCount
                additionalInfo
                foundUs
                utmSource
                bookedLessonCount
            }
        }
    }
`;

// PERFORMANCE OPTIMIERUNG: Beste Balance zwischen Einfachheit und Performance
// - Enthält course (für Light-Spalten) UND courseLessons (für Heavy-Spalten)
// - courseLessons minimal optimiert (nur 3 Felder statt 10+)
// - Keine Query-Duplizierung durch Switching
// - ~50% schneller als alte Version trotz beiden Feldern
export const GET_BOOKINGS_OVERVIEW_OPTIMIZED = gql`
    query GetBookingsOverviewOptimized($options: ArrayDataOptions, $onlyCurrentUser: Boolean) {
        bookings(options: $options, onlyCurrentUser: $onlyCurrentUser) {
            existsMore
            total
            items {
                id
                isSettled
                bookingNumber
                bookingNumberPrefix
                bookingType
                updateDate
                createDate
                startDateTime
                courseId
                noticeStaff
                attendanceListNote
                noticeBooker
                coursePriceId
                payDate
                grossPrice
                netPrice
                vat
                pricingReason
                isSpecial
                paymentType
                bookedBy
                discountCodeText
                canceled
                waitListCount
                additionalInfo
                foundUs
                utmSource
                bookedLessonCount
                bookingConfirmationSent
                waitingListConfirmationSent
                cancelationSent
                invoiceSent

                paymentMethod {
                    id
                    type
                    cash
                    esr
                    iban
                    bank
                    bic
                    sepaAccountholder
                    bankTransfer
                    onlinePayment
                }

                course {
                    id
                    prefixedCourseNumber
                    allowLessonBooking
                    lessonCount
                    paymentInterval
                    freePlaces
                    maxAttendees
                    startDateTime
                    endDateTime
                    endDateTimeFirstCourseLesson
                    courseType {
                        id
                        name
                        color
                    }
                    location {
                        id
                        city
                        name
                    }
                    instructors {
                        id
                        firstname
                        lastname
                    }
                }

                courseLessons {
                    id
                    startDateTime
                    endDateTime
                    indexOfLessonInCourse
                }

                courseLessonBookings {
                    id
                    courseLessonId
                    bookingType
                    bookingDate
                    attendance
                }

                scheduledPayments {
                    paymentProvider
                    mollieMethod
                }

                coursePrice {
                    id
                    grossPrice
                    netPrice
                    vat
                }

                invoice {
                    id
                    billed
                    invoiceNumber
                    invoiceNumberPrefix
                    createDate
                }

                reversalInvoice {
                    id
                    invoiceNumber
                    invoiceNumberPrefix
                    createDate
                }

                attendee {
                    id
                    erasedAt
                    gender
                    firstname
                    lastname
                    birthday
                    customerNumberPrefix
                    customerNumber
                    member

                    booker {
                        id
                        gender
                        firstname
                        lastname
                        customerNumberPrefix
                        customerNumber
                        email
                        city
                        credit
                        phoneNumber
                        mobileNumber
                        nonDisclosureNotice
                    }
                }

                paymentMethod {
                    id
                    type
                }

                emailNotifications {
                    id
                    templateType
                    subject
                    createDate
                    bookingId
                    email
                }
            }
        }
    }
`;

export const GET_BOOKINGS_DYNAMIC = gql`
    fragment BookingCoreFields on Booking {
        id
        bookingNumber
        bookingNumberPrefix
        bookingType
        updateDate
        createDate
        startDateTime
        courseId
        noticeStaff
        attendanceListNote
        noticeBooker
        coursePriceId
        payDate
        grossPrice
        netPrice
        vat
        isSpecial
        paymentType
        bookedBy
        canceled
        bookingConfirmationSent
        waitingListConfirmationSent
        cancelationSent
        invoiceSent
        waitListCount
        additionalInfo
    }

    fragment BookingCoursePriceFields on Booking {
        coursePrice {
            id
            grossPrice
            netPrice
            vat
        }
    }

    fragment BookingInvoiceFields on Booking {
        invoice {
            id
            billed
            invoiceNumber
            invoiceNumberPrefix
            createDate
        }
    }

    fragment BookingReversalInvoiceFields on Booking {
        reversalInvoice {
            id
            invoiceNumber
            invoiceNumberPrefix
            createDate
        }
    }

    fragment BookingCourseLessonsFields on Booking {
        courseLessons {
            id
            startDateTime
            endDateTime
            locationId
            course {
                id
                freePlaces
                startDateTime
                paymentInterval
                minAttendees
                maxAttendees
                prefixedCourseNumber
                allowMonthlyPayment
                paymentInterval
                courseType {
                    id
                    name
                    color
                }
                instructors {
                    id
                    firstname
                    lastname
                }
                lessonCount
                allowLessonBooking
                allowMolliePayment
                mollieOptional
            }
            location {
                id
                city
                name
            }
        }
    }

    fragment BookingAttendeeFields on Booking {
        attendee {
            id
            erasedAt
            gender
            firstname
            lastname
            birthday
            customerNumberPrefix
            customerNumber
            member

            booker {
                id
                gender
                firstname
                lastname
                customerNumberPrefix
                customerNumber
                street
                houseNumber
                postCode
                city
                phoneNumber
                mobileNumber
                email
                gender
                nonDisclosureNotice
                credit
            }
        }
    }

    fragment BookingEmailNotificationsFields on Booking {
        emailNotifications {
            id
            templateType
            subject
            createDate
            bookingId
            email
        }
    }

    fragment BookingPaymentMethodFields on Booking {
        paymentMethod {
            id
            type
            cash
            esr
            iban
            bank
            bic
            sepaAccountholder
            bankTransfer
            onlinePayment
        }
    }

    query GetBookingsDynamic(
        $options: ArrayDataOptions
        $includeCoursePrice: Boolean!
        $includeInvoice: Boolean!
        $includeReversalInvoice: Boolean!
        $includeCourseLessons: Boolean!
        $includeAttendee: Boolean!
        $includeEmailNotifications: Boolean!
        $includePaymentMethod: Boolean!
    ) {
        bookingsDynamic(
            options: $options
            #dynamicIncludeOptions: $dynamicIncludeOptions
            includeCoursePrice: $includeCoursePrice
            includeInvoice: $includeInvoice
            includeReversalInvoice: $includeReversalInvoice
            includeCourseLessons: $includeCourseLessons
            includeAttendee: $includeAttendee
            includeEmailNotifications: $includeEmailNotifications
            includePaymentMethod: $includePaymentMethod
        ) {
            existsMore
            total
            items {
                ...BookingCoreFields
                ...BookingCoursePriceFields @include(if: $includeCoursePrice)
                ...BookingInvoiceFields @include(if: $includeInvoice)
                ...BookingReversalInvoiceFields @include(if: $includeReversalInvoice)
                ...BookingCourseLessonsFields @include(if: $includeCourseLessons)
                ...BookingAttendeeFields @include(if: $includeAttendee)
                ...BookingEmailNotificationsFields @include(if: $includeEmailNotifications)
                ...BookingPaymentMethodFields @include(if: $includePaymentMethod)
            }
        }
    }
`;

export const CREATE_BOOKING = gql`
    mutation CreateBooking($newBookingData: NewBookingInput!, $sendMail: Boolean!) {
        createBooking(newBookingData: $newBookingData, sendMail: $sendMail) {
            id
            bookingType
            paymentType
            scheduledPayments {
                id
                paymentAmount
                paymentDue
                paymentDone
                paymentProvider
                mollieMethod
                type
            }
        }
    }
`;

export const REMOVE_BOOKING = gql`
    mutation RemoveBooking($id: ID!, $bookingDeleteAction: BookingDeleteAction, $notifyBooker: Boolean) {
        removeBooking(id: $id, bookingDeleteAction: $bookingDeleteAction, notifyBooker: $notifyBooker)
    }
`;

export const REMOVE_BOOKINGS = gql`
    mutation RemoveBookings($ids: [ID!]!) {
        removeBookings(ids: $ids)
    }
`;

export const UPDATE_BOOKING_BY_ID = gql`
    mutation UpdateBooking($editBookingData: EditBookingInput!, $id: String!) {
        updateBooking(editBookingData: $editBookingData, id: $id) {
            id
            bookingType
        }
    }
`;

export const UPDATE_NOTICE_STAFF = gql`
    mutation UpdateNoticeStaff($id: String!, $noticeStaff: String!) {
        updateNoticeStaff(id: $id, noticeStaff: $noticeStaff) {
            id
            noticeStaff
        }
    }
`;

export const GET_MONTHLY_PAYMENT_PREVIEW = gql`
    query getMonthlyPaymentPreview($monthlyPaymentPreviewOptions: ScheduledPaymentPreviewInput!) {
        getMonthlyPaymentPreview(monthlyPaymentPreviewOptions: $monthlyPaymentPreviewOptions)
    }
`;

export const GET_BOOKING_BY_ID = gql`
    query getBookingById($id: ID!) {
        bookingById(id: $id) {
            isSettled
            transferIn {
                sourceBookingNumber
                appliedAmount
            }
            id
            bookingNumber
            bookingNumberPrefix
            bookingType
            updateDate
            createDate
            noticeStaff
            noticeBooker
            coursePriceId
            payDate
            grossPrice
            netPrice
            vat
            pricingReason
            pricingCalculatedAt
            pricingSnapshot
            lessonSelectionMode
            memberDiscount
            discount
            discountName
            hasDiscountCode
            isSpecial
            startDateTime
            courseId
            paymentMethodId
            bookedBy
            canceled
            cancelRestHandling
            cancelRestAmount
            cancelSettledAmount
            cancelRestNote
            cancelWaivedFees {
                title
                amount
            }
            monthlySchedule
            paymentType
            creditedBalance
            waitListCount
            additionalInfo
            foundUs
            bookedLessonCount
            coursePrice {
                id
                grossPrice
                netPrice
                vat
            }
            invoice {
                billed
                invoiceNumber
                invoiceNumberPrefix
                createDate
                correctionDate
                createDate
                id
            }
            reversalInvoice {
                id
                invoiceNumber
                invoiceNumberPrefix
                createDate
            }
            courseLessons {
                startDateTime
                endDateTime
                id
                locationId
                course {
                    id
                    freePlaces
                    bookingCount
                    startDateTime
                    paymentInterval
                    minAttendees
                    maxAttendees
                    prefixedCourseNumber
                    allowMonthlyPayment
                    allowUpFrontPayment
                    monthlySchedule
                    courseType {
                        id
                        name
                        color
                    }
                    coursePrice {
                        id
                        grossPrice
                        netPrice
                        vat
                        discount
                        discountUnit
                        isSpecial
                    }
                    installmentRates {
                        id
                        installmentAmount
                        installmentDue
                    }
                    paymentType
                    lessons {
                        id
                        startDateTime
                    }
                    allowLessonBooking
                    allowMolliePayment
                    mollieOptional
                }
                location {
                    id
                    city
                    name
                }
            }
            attendee {
                id
                erasedAt
                gender
                firstname
                lastname
                birthday
                customerNumberPrefix
                customerNumber
                member
                hasBillingAddress
                billingCompanyName
                billingFirstname
                billingLastname
                billingAddition
                billingStreet
                billingPostCode
                billingCity
                booker {
                    id
                    firstname
                    lastname
                    customerNumberPrefix
                    customerNumber
                    street
                    houseNumber
                    postCode
                    city
                    phoneNumber
                    mobileNumber
                    email
                    gender
                    hasBillingAddress
                    billingCompanyName
                    billingFirstname
                    billingLastname
                    billingAddition
                    billingStreet
                    billingPostCode
                    billingCity
                    paymentMethods {
                        id
                        type
                        cash
                        esr
                        iban
                        bank
                        bic
                        sepaAccountholder
                        bankTransfer
                        onlinePayment
                    }
                    nonDisclosureNotice
                    credit
                }
                defaultPaymentMethod {
                    id
                    cash
                    esr
                    iban
                    bank
                    bic
                    sepaAccountholder
                }
            }
            paymentMethod {
                id
                cash
                esr
                creditcardNumber
                creditCardAccountholder
                expirationDate
                sepaAccountholder
                iban
                bic
                bank
                paypalAccountholder
                email
                bankTransfer
                onlinePayment
            }
            scheduledPayments {
                id
                paymentAmount
                refundedAmount
                providerRefundedAmount
                molliePaidAmount
                paymentDue
                paymentDone
                openAmount
                keptOnCancelAt
                type
                title
                postKind
                carriedOverAt
                additionalItemId
                vat
                paymentProvider
                mollieMethod
            }
            # MolliePaymentStatusPanel (unsettled Booking-Edit) braucht den aktuellen
            # Mollie-Zahlungsversuch je Rate, um Status + Ablauf anzuzeigen und den
            # Resend-Button zustandsabhängig zu schalten.
            # Bewusst INLINE statt via ...BookingMolliePaymentAttemptsFields-Spread:
            # die Apollo-Cache läuft mit addTypename:false, wodurch Named-Fragment-
            # Spreads beim Cache-Read verloren gehen (siehe GET_BOOKINGS_BY_COURSE
            # oben + lessonBooking.graphql.ts).
            molliePaymentAttempts {
                id
                scheduledPaymentId
                status
                mollieMethod
                linkExpiresAt
                checkoutUrl
                isCurrentAttempt
                createDate
            }
        }
    }
`;

export const GET_BOOKINGS_BY_COURSE = gql`
    query GetBookingsByCourse($options: ArrayDataOptions, $id: ID!) {
        getBookingsByCourse(options: $options, id: $id) {
            existsMore
            total
            items {
                id
                bookingNumber
                bookingNumberPrefix
                bookingType
                updateDate
                createDate
                startDateTime
                courseId
                noticeStaff
                attendanceListNote
                noticeBooker
                coursePriceId
                payDate
                grossPrice
                netPrice
                vat
                isSpecial
                paymentType
                bookedBy
                canceled
                waitListCount
                additionalInfo
                foundUs
                bookedLessonCount
                lessonSelectionMode
                bookingConfirmationSent
                waitingListConfirmationSent
                cancelationSent
                invoiceSent

                course {
                    id
                    prefixedCourseNumber
                    allowLessonBooking
                    lessonCount
                    paymentInterval
                    freePlaces
                    maxAttendees
                    startDateTime
                    endDateTime
                    courseType {
                        id
                        name
                        color
                    }
                    location {
                        id
                        city
                        name
                    }
                    instructors {
                        id
                        firstname
                        lastname
                    }
                }

                courseLessons {
                    id
                    startDateTime
                    endDateTime
                    indexOfLessonInCourse
                }

                courseLessonBookings {
                    id
                    courseLessonId
                    bookingType
                    bookingDate
                    attendance
                }

                coursePrice {
                    id
                    grossPrice
                    netPrice
                    vat
                }

                invoice {
                    id
                    billed
                    invoiceNumber
                    invoiceNumberPrefix
                    createDate
                }

                reversalInvoice {
                    id
                    invoiceNumber
                    invoiceNumberPrefix
                    createDate
                }

                attendee {
                    id
                    erasedAt
                    gender
                    firstname
                    lastname
                    birthday
                    customerNumberPrefix
                    customerNumber
                    member
                    booker {
                        id
                        gender
                        firstname
                        lastname
                        customerNumberPrefix
                        customerNumber
                        email
                        city
                        street
                        houseNumber
                        postCode
                        credit
                        phoneNumber
                        mobileNumber
                        nonDisclosureNotice
                    }
                    badgeXAttendee {
                        id
                        createDate
                        dateOfAchivement
                        badge {
                            id
                            name
                            abbreviation
                            color
                        }
                    }
                }

                paymentMethod {
                    id
                    type
                }

                emailNotifications {
                    id
                    templateType
                    subject
                    createDate
                    bookingId
                    email
                }

                # Transfer-FK / Link-Void: Bulk-Pre-Flight braucht die Attempt-Link-Felder,
                # um beim Sammelumbuchen Buchungen mit live zahlbarem Mollie-Zahlungslink
                # (entwertbar) bzw. bezahltem Link (nicht übertragbar) zu erkennen.
                # Bewusst INLINE statt via ...BookingMolliePaymentAttemptsFields-Spread:
                # die Apollo-Cache läuft mit addTypename:false, wodurch Named-Fragment-
                # Spreads beim Cache-Read verloren gehen (siehe lessonBooking.graphql.ts).
                molliePaymentAttempts {
                    id
                    molliePaymentLinkId
                    linkExpiresAt
                    status
                    paidAt
                    isCurrentAttempt
                    createDate
                }
            }
        }
    }
`;

// export const GET_BOOKINGS_BY_COURSE = gql`
//     query GetBookingsByCourse($options: ArrayDataOptions, $id: ID!) {
//         getBookingsByCourse(options: $options, id: $id) {
//             existsMore
//             total
//             items {
//                 id
//                 bookingNumber
//                 bookingNumberPrefix
//                 bookingType
//                 updateDate
//                 createDate
//                 noticeStaff
//                 noticeBooker
//                 attendanceListNote
//                 coursePriceId
//                 courseId
//                 noticeBooker
//                 payDate
//                 grossPrice
//                 netPrice
//                 vat
//                 isSpecial
//                 courseLessonBookings {
//                     id
//                     bookingId
//                     attendance
//                     courseLessonId
//                 }
//                 coursePrice {
//                     id
//                     grossPrice
//                     netPrice
//                     vat
//                 }
//                 invoice {
//                     id
//                     billed
//                     invoiceNumber
//                     invoiceNumberPrefix
//                     createDate
//                 }
//                 reversalInvoice {
//                     id
//                     invoiceNumber
//                     invoiceNumberPrefix
//                     createDate
//                 }
//                 paymentType
//                 # course {
//                 #     id
//                 #     startDateTime
//                 # }
//                 courseLessons {
//                     id
//                     startDateTime
//                     endDateTime
//                     locationId
//                     course {
//                         id
//                         freePlaces
//                         startDateTime
//                         paymentInterval
//                         minAttendees
//                         maxAttendees
//                         prefixedCourseNumber
//                         allowMonthlyPayment
//                         courseType {
//                             id
//                             name
//                             color
//                         }
//                         allowLessonBooking
//                         lessonCount
//                     }
//                     location {
//                         id
//                         city
//                         name
//                     }
//                     #courseLessonBookings {
//                     #    id
//                     #    bookingId
//                     #    attendance
//                     #    courseLessonId
//                     #}
//                 }
//                 attendee {
//                     id
//                     gender
//                     firstname
//                     lastname
//                     birthday
//                     customerNumberPrefix
//                     customerNumber
//                     booker {
//                         id
//                         gender
//                         firstname
//                         lastname
//                         customerNumberPrefix
//                         customerNumber
//                         street
//                         houseNumber
//                         postCode
//                         city
//                         phoneNumber
//                         mobileNumber
//                         email
//                         gender
//                         nonDisclosureNotice
//                         credit
//                     }
//                     badgeXAttendee {
//                         id
//                         createDate
//                         dateOfAchivement
//                         badge {
//                             id
//                             name
//                             abbreviation
//                             color
//                         }
//                     }
//                 }
//                 bookedBy
//                 canceled
//                 emailNotifications {
//                     id
//                     templateType
//                     subject
//                     createDate
//                     bookingId
//                     email
//                 }
//                 bookingConfirmationSent
//                 waitingListConfirmationSent
//                 cancelationSent
//                 invoiceSent
//                 paymentMethod {
//                     id
//                     type
//                     cash
//                     esr
//                     iban
//                     bank
//                     bic
//                     sepaAccountholder
//                     bankTransfer
//                 }
//                 waitListCount
//                 additionalInfo
//                 bookedLessonCount
//             }
//         }
//     }
// `;

export const GET_WAITLIST_BY_COURSE = gql`
    query GetWaitlistByCourse($options: ArrayDataOptions, $id: ID!) {
        getWaitlistByCourse(options: $options, id: $id) {
            existsMore
            total
            items {
                id
                bookingNumber
                bookingNumberPrefix
                bookingType
                coursePriceId
                courseId
                grossPrice
                netPrice
                vat
                isSpecial
                coursePrice {
                    id
                    grossPrice
                    netPrice
                    vat
                }
                courseLessons {
                    id
                    startDateTime
                    endDateTime
                    course {
                        id
                        paymentInterval
                        allowLessonBooking
                    }
                }
                paymentType
                attendee {
                    id
                    erasedAt
                    gender
                    firstname
                    lastname
                    birthday
                    customerNumberPrefix
                    customerNumber
                    member
                    booker {
                        id
                        gender
                        firstname
                        lastname
                        customerNumberPrefix
                        customerNumber
                        street
                        houseNumber
                        postCode
                        city
                        phoneNumber
                        mobileNumber
                        email
                        gender
                        nonDisclosureNotice
                    }
                }
                canceled
                waitListCount
                bookedLessonCount
            }
        }
    }
`;

export const GET_WAITLIST_COUNT_OF_COURSE = gql`
    query GetWaitlistCountOfCourse($id: ID!) {
        getWaitlistCountOfCourse(id: $id)
    }
`;

export const GET_BOOKINGS_BY_COURSE_MINIMAL = gql`
    query GetBookingsByCourseMinimal($options: ArrayDataOptions, $id: ID!) {
        getBookingsByCourse(options: $options, id: $id) {
            existsMore
            total
            items {
                id
                bookingNumber
                bookingNumberPrefix
                bookingType
                attendee {
                    id
                    gender
                    birthday
                    firstname
                    lastname
                    customerNumberPrefix
                    customerNumber
                    member
                    booker {
                        id
                        firstname
                        lastname
                        gender
                        customerNumberPrefix
                        customerNumber
                        email
                        nonDisclosureNotice
                    }
                }
                canceled
                waitListCount
            }
        }
    }
`;

export const GET_BOOKINGS_BY_COURSE_FOR_TRANSFER = gql`
    query GetBookingsByCourseForTransfer($options: ArrayDataOptions, $id: ID!) {
        getBookingsByCourse(options: $options, id: $id) {
            existsMore
            total
            items {
                id
                bookingNumber
                bookingNumberPrefix
                bookingType
                attendee {
                    id
                    gender
                    birthday
                    firstname
                    lastname
                    customerNumberPrefix
                    customerNumber
                    member
                    booker {
                        id
                        firstname
                        lastname
                        gender
                        customerNumberPrefix
                        customerNumber
                        email
                        nonDisclosureNotice
                    }
                }
                canceled
                waitListCount
                invoice {
                    id
                }
                bookedLessonCount
            }
        }
    }
`;

export const GET_BOOKINGS_COUNT_BY_COURSE = gql`
    query GetBookingsCountByCourse($options: ArrayDataOptions, $id: ID!) {
        getBookingsByCourse(options: $options, id: $id) {
            existsMore
            total
            items {
                id
                bookingNumberPrefix
                bookingType
                canceled
                waitListCount
            }
        }
    }
`;

export const REMOVE_ALL_BOOKINGS = gql`
    mutation RemoveAllBookings {
        removeAllBookings
    }
`;

export const SEND_BOOKING_CONFIRMATION = gql`
    mutation SendBookingConfirmation($bookingId: ID!, $editedTemplateInput: EditTemplateInput) {
        sendBookingConfirmation(bookingId: $bookingId, editedTemplateInput: $editedTemplateInput)
    }
`;

export const GET_VARIABLES_FOR_BOOKING_WITH_COURSE = gql`
    query getVariablesForBookingWithCourse($id: ID!) {
        bookingById(id: $id) {
            id
            attendee {
                id
                firstname
                lastname
                birthday
                member
                booker {
                    id
                    firstname
                    lastname
                }
            }
            courseLessons {
                id
                startDateTime
                endDateTime
                course {
                    id
                    prefixedCourseNumber
                    courseType {
                        id
                        name
                        description
                    }
                    allowLessonBooking
                }
                location {
                    id
                    city
                    name
                }
            }
            creditedBalance
            memberDiscount
            discount
            discountName
            bookedLessonCount
        }
    }
`;

export const CONVERT_BOOKING_TO_WAITLIST = gql`
    mutation convertBookingToWaitlist($bookingId: ID!) {
        convertBookingToWaitlist(bookingId: $bookingId) {
            id
            bookingType
            bookingNumberPrefix
            bookingNumber
            attendee {
                id
                firstname
                lastname
                member
            }
        }
    }
`;

export const CONVERT_WAITLIST_TO_BOOKING = gql`
    mutation convertWaitlistToBooking($bookingId: ID!) {
        convertWaitlistToBooking(bookingId: $bookingId) {
            id
            bookingType
            bookingNumberPrefix
            bookingNumber
            attendee {
                id
                firstname
                lastname
                member
            }
        }
    }
`;

export const TRANSFER_ATTENDEE_TO_COURSE = gql`
    mutation transferAttendeeToCourse($bookingId: ID!, $targetCourseId: ID!, $bookingDeleteAction: BookingDeleteAction, $usePlaceReservation: Boolean, $deleteNotes: Boolean) {
        transferAttendeeToCourse(
            bookingId: $bookingId
            targetCourseId: $targetCourseId
            bookingDeleteAction: $bookingDeleteAction
            usePlaceReservation: $usePlaceReservation
            deleteNotes: $deleteNotes
        ) {
            id
            bookingType
            bookingNumberPrefix
            bookingType
            grossPrice
            netPrice
            vat
            noticeStaff
            attendee {
                id
                firstname
                lastname
                member
            }
            courseLessons {
                id
                course {
                    id
                    prefixedCourseNumber
                    courseType {
                        id
                        name
                    }
                    allowLessonBooking
                }
            }
            bookedLessonCount
            scheduledPayments {
                id
                paymentAmount
                paymentDue
                paymentDone
                openAmount
            }
        }
    }
`;

// Transfer-FK / Link-Void: Entwertet die live zahlbaren Mollie-Zahlungslinks einer
// einzelnen Buchung aktiv bei Mollie, bevor sie aus einem Mollie-Kurs umgebucht wird.
// Wird vom Single-Transfer-Dialog nach dem MOLLIE_ACTIVE_PAYMENT_LINK-Fehler aufgerufen.
export const VOID_MOLLIE_PAYMENT_LINK_FOR_BOOKING = gql`
    mutation VoidMolliePaymentLinkForBooking($bookingId: ID!) {
        voidMolliePaymentLinkForBooking(bookingId: $bookingId) {
            id
        }
    }
`;

// Transfer-FK / Link-Void: Batch-Variante für den Bulk-Pre-Flight (Sammelumbuchen).
// Liefert pro Buchung ein {bookingId, voided, error}-Ergebnis (Best-Effort).
export const VOID_MOLLIE_PAYMENT_LINKS_FOR_BOOKINGS = gql`
    mutation VoidMolliePaymentLinksForBookings($bookingIds: [ID!]!) {
        voidMolliePaymentLinksForBookings(bookingIds: $bookingIds) {
            bookingId
            voided
            error
        }
    }
`;

export const COPY_ATTENDEE_TO_COURSE = gql`
    mutation copyAttendeeToCourse($bookingId: ID!, $targetCourseId: ID!, $usePlaceReservation: Boolean, $deleteNotes: Boolean) {
        copyAttendeeToCourse(bookingId: $bookingId, targetCourseId: $targetCourseId, usePlaceReservation: $usePlaceReservation, deleteNotes: $deleteNotes) {
            id
            bookingType
            bookingNumberPrefix
            bookingType
            attendee {
                id
                firstname
                lastname
                member
            }
            courseLessons {
                id
                course {
                    id
                    prefixedCourseNumber
                    courseType {
                        id
                        name
                    }
                    allowLessonBooking
                }
            }
            bookedLessonCount
        }
    }
`;

export const GET_BOOKINGS_BY_IDS = gql`
    query GetBookingsByIds($ids: [String!]!) {
        getBookingsByIds(ids: $ids) {
            id
            bookingNumberPrefix
            bookingNumber
            bookingType
            courseId
            attendee {
                id
                member
                booker {
                    id
                    firstname
                    lastname
                    email
                    mobileNumber
                }
            }
            canceled
            waitListCount
            bookedLessonCount
        }
    }
`;

export const GET_BOOKINGS_BY_SCHEDULEDPAYMENT_IDS = gql`
    query GetBookingsByScheduledPaymentIds($scheduledPaymentIds: [String!]!) {
        getBookingsByScheduledPaymentIds(scheduledPaymentIds: $scheduledPaymentIds) {
            id
            bookingNumberPrefix
            bookingNumber
            bookingType
            attendee {
                id
                member
                booker {
                    id
                    firstname
                    lastname
                    email
                }
            }
            canceled
            bookedLessonCount
        }
    }
`;

export const GET_BOOKINGS_BY_INVOICE_IDS = gql`
    query getBookingsByInvoiceIds($invoiceIds: [String!]!) {
        getBookingsByInvoiceIds(invoiceIds: $invoiceIds) {
            id
            bookingNumberPrefix
            bookingNumber
            bookingType
            #attendee {
            #    id
            #    firstname
            #    lastname
            #    booker {
            #        id
            #        firstname
            #        lastname
            #        email
            #}
            bookedLessonCount
        }
    }
`;

export const GET_BOOKINGS_BY_ATTENDEE_WITH_OPTIONS = gql`
    query GetBookingsByAttendeeIdWithOptions($id: ID!, $startDateTime: DateTime, $endDateTime: DateTime, $canceledBookings: Boolean) {
        getBookingsByAttendeeIdWithOptions(id: $id, startDateTime: $startDateTime, endDateTime: $endDateTime, canceledBookings: $canceledBookings) {
            id
            isSettled
            bookingNumber
            bookingNumberPrefix
            bookingType
            updateDate
            createDate
            startDateTime
            courseId
            noticeStaff
            attendanceListNote
            noticeBooker
            coursePriceId
            payDate
            grossPrice
            netPrice
            vat
            isSpecial
            paymentType
            bookedBy
            canceled
            waitListCount
            additionalInfo
            bookedLessonCount
            bookingConfirmationSent
            waitingListConfirmationSent
            cancelationSent
            invoiceSent

            course {
                id
                prefixedCourseNumber
                allowLessonBooking
                lessonCount
                paymentInterval
                freePlaces
                maxAttendees
                startDateTime
                endDateTime
                endDateTimeFirstCourseLesson
                courseType {
                    id
                    name
                    color
                }
                location {
                    id
                    city
                    name
                }
                instructors {
                    id
                    firstname
                    lastname
                }
            }

            courseLessons {
                id
                startDateTime
                endDateTime
                indexOfLessonInCourse
            }

            courseLessonBookings {
                id
                courseLessonId
                bookingType
                bookingDate
                attendance
            }

            coursePrice {
                id
                grossPrice
                netPrice
                vat
            }

            invoice {
                id
                billed
                invoiceNumber
                invoiceNumberPrefix
                createDate
            }

            reversalInvoice {
                id
                invoiceNumber
                invoiceNumberPrefix
                createDate
            }

            attendee {
                id
                erasedAt
                gender
                firstname
                lastname
                birthday
                customerNumberPrefix
                customerNumber
                member

                booker {
                    id
                    gender
                    firstname
                    lastname
                    customerNumberPrefix
                    customerNumber
                    email
                    city
                    credit
                    phoneNumber
                    mobileNumber
                    nonDisclosureNotice
                }
            }

            paymentMethod {
                id
                type
                cash
                esr
                iban
                bank
                bic
                sepaAccountholder
                bankTransfer
                onlinePayment
            }

            emailNotifications {
                id
                templateType
                subject
                createDate
                bookingId
                email
            }
        }
    }
`;

export const UPDATE_ATTENDANCE_LIST_NOTE = gql`
    mutation UpdateAttendanceListNote($bookingId: ID!, $attendanceListNote: String) {
        updateAttendanceListNote(bookingId: $bookingId, attendanceListNote: $attendanceListNote) {
            id
            attendanceListNote
        }
    }
`;

export const UPDATE_BOOKING_PAYMENT_METHOD = gql`
    mutation UpdateBookingPaymentMethod($bookingId: ID!, $paymentMethodId: ID!) {
        updateBookingPaymentMethod(bookingId: $bookingId, paymentMethodId: $paymentMethodId) {
            id
            paymentMethodId
        }
    }
`;

export const ADD_MULTIPLE_PLACE_RESERVATIONS = gql`
    mutation AddMultiplePlaceReservations($courseId: ID!, $count: Float!) {
        addMultiplePlaceReservations(courseId: $courseId, count: $count) {
            id
        }
    }
`;

export const DELETE_ALL_PLACE_RESERVATIONS_BY_COURSE_ID = gql`
    mutation DeleteAllPlaceReservationsByCourseId($courseId: ID!) {
        deleteAllPlaceReservationsByCourseId(courseId: $courseId)
    }
`;

export const DELETE_SELECTION_OF_WAITLIST = gql`
    mutation DeleteSelectionOfWaitlist($bookingIds: [ID!]!) {
        deleteSelectionOfWaitlist(bookingIds: $bookingIds)
    }
`;

export const GET_BOOKINGS_BY_COURSE_REFACTORED = gql`
    query GetBookingsByCourseRefactored($options: ArrayDataOptions, $id: ID!) {
        getBookingsByCourse(options: $options, id: $id) {
            existsMore
            total
            items {
                id
                isSettled
                bookingNumber
                bookingNumberPrefix
                bookingType
                updateDate
                createDate
                noticeStaff
                noticeBooker
                attendanceListNote
                coursePriceId
                courseId
                noticeBooker
                payDate
                grossPrice
                netPrice
                vat
                isSpecial
                coursePrice {
                    id
                    grossPrice
                    netPrice
                    vat
                }
                invoice {
                    id
                    billed
                    invoiceNumber
                    invoiceNumberPrefix
                    createDate
                }
                reversalInvoice {
                    id
                    invoiceNumber
                    invoiceNumberPrefix
                    createDate
                }
                paymentType
                courseLessons {
                    id
                    startDateTime
                    endDateTime
                    locationId
                    course {
                        id
                        freePlaces
                        startDateTime
                        endDateTimeFirstCourseLesson
                        paymentInterval
                        minAttendees
                        maxAttendees
                        prefixedCourseNumber
                        allowMonthlyPayment
                        courseType {
                            id
                            name
                            color
                        }
                        allowLessonBooking
                    }
                    location {
                        id
                        city
                        name
                    }
                    #courseLessonBookings {
                    #    id
                    #    bookingId
                    #    attendance
                    #    courseLessonId
                    #}
                }
                attendee {
                    id
                    erasedAt
                    gender
                    firstname
                    lastname
                    birthday
                    customerNumberPrefix
                    customerNumber
                    member
                    booker {
                        id
                        gender
                        firstname
                        lastname
                        customerNumberPrefix
                        customerNumber
                        street
                        houseNumber
                        postCode
                        city
                        phoneNumber
                        mobileNumber
                        email
                        gender
                        nonDisclosureNotice
                        credit
                    }
                }
                bookedBy
                canceled
                emailNotifications {
                    id
                    templateType
                    subject
                    createDate
                    bookingId
                    email
                }
                bookingConfirmationSent
                waitingListConfirmationSent
                cancelationSent
                invoiceSent
                paymentMethod {
                    id
                    type
                    cash
                    esr
                    iban
                    bank
                    bic
                    sepaAccountholder
                    bankTransfer
                    onlinePayment
                }
                waitListCount
                bookedLessonCount
            }
        }
    }
`;

export const TRANSFER_MULTIPLE_BOOKINGS = gql`
    mutation TransferMultipleBookings($transferMultipleBookingsInput: TransferMultipleBookingsInput!, $deleteNotes: Boolean) {
        transferMultipleBookings(transferMultipleBookingsInput: $transferMultipleBookingsInput, deleteNotes: $deleteNotes) {
            id
            bookingNumberPrefix
            bookingNumber
            grossPrice
            courseId
            scheduledPayments {
                id
                paymentDue
                paymentAmount
                type
            }
            attendee {
                id
                firstname
                lastname
                member
            }
        }
    }
`;
// bookingsInStartedCourses
export const GET_BOOKINGS_IN_STARTED_COURSES = gql`
    query GetBookingsInStartedCourses($options: ArrayDataOptions) {
        bookingsInStartedCourses(options: $options) {
            existsMore
            total
            items {
                id
                isSettled
                bookingNumber
                bookingNumberPrefix
                bookingType
                updateDate
                createDate
                startDateTime
                courseId
                noticeStaff
                attendanceListNote
                noticeBooker
                coursePriceId
                payDate
                grossPrice
                netPrice
                vat
                isSpecial
                paymentType
                bookedBy
                canceled
                waitListCount
                additionalInfo
                foundUs
                bookedLessonCount
                bookingConfirmationSent
                waitingListConfirmationSent
                cancelationSent
                invoiceSent

                course {
                    id
                    prefixedCourseNumber
                    allowLessonBooking
                    lessonCount
                    paymentInterval
                    freePlaces
                    maxAttendees
                    startDateTime
                    endDateTime
                    endDateTimeFirstCourseLesson
                    courseType {
                        id
                        name
                        color
                    }
                    location {
                        id
                        city
                        name
                    }
                    instructors {
                        id
                        firstname
                        lastname
                    }
                }

                courseLessons {
                    id
                    startDateTime
                    endDateTime
                    indexOfLessonInCourse
                }

                courseLessonBookings {
                    id
                    courseLessonId
                    bookingType
                    bookingDate
                    attendance
                }

                coursePrice {
                    id
                    grossPrice
                    netPrice
                    vat
                }

                invoice {
                    id
                    billed
                    invoiceNumber
                    invoiceNumberPrefix
                    createDate
                }

                reversalInvoice {
                    id
                    invoiceNumber
                    invoiceNumberPrefix
                    createDate
                }

                attendee {
                    id
                    erasedAt
                    gender
                    firstname
                    lastname
                    birthday
                    customerNumberPrefix
                    customerNumber
                    member
                    booker {
                        id
                        gender
                        firstname
                        lastname
                        customerNumberPrefix
                        customerNumber
                        email
                        city
                        credit
                        phoneNumber
                        mobileNumber
                        nonDisclosureNotice
                    }
                }

                paymentMethod {
                    id
                    type
                }

                emailNotifications {
                    id
                    templateType
                    subject
                    createDate
                    bookingId
                    email
                }
            }
        }
    }
`;

// ════════════════════════════════════════════════════════════════════════════════
// Phase 23 / Plan 23-04 / BDSEC-09 — MolliePaymentAttempts-Selection
// ════════════════════════════════════════════════════════════════════════════════
//
// Re-usable Fragment fuer die `Booking.molliePaymentAttempts`-Selection.
// Wird in `GET_BOOKING_BY_ID_WITH_COURSE_LESSON_BOOKINGS` (lessonBooking.graphql.ts)
// via Interpolation eingebunden — liefert in einem Roundtrip alle Attempt-,
// Refund- und Chargeback-Daten fuer die MolliePaymentSection (Plan 23-05).
//
// Server-Seite: Field-Resolver `Booking.molliePaymentAttempts` + nested
// `MolliePaymentAttempt.refunds` und `.chargebacks` (Plan 23-02).
// Sort-Order der Attempts: server-side `createDate DESC` (Pitfall 8).
//
// Note: `currency` ist NICHT im `MollieRefund`-Schema exposed (Plan 23-03
// Deviation — Field hat kein @Field-Decorator) — Selection laesst es weg.
// `failureReason` analog (Plan 23-03 Deviation 3) — nicht selektiert.
export const BOOKING_MOLLIE_PAYMENT_ATTEMPTS_FIELDS = gql`
    fragment BookingMolliePaymentAttemptsFields on Booking {
        molliePaymentAttempts {
            id
            molliePaymentId
            scheduledPaymentId
            molliePaymentLinkId
            bundleScheduledPaymentIds
            mollieMethod
            status
            checkoutUrl
            paymentUrl
            isCurrentAttempt
            amount
            linkExpiresAt
            paidAt
            createDate
            errorMessage
            chargebackId
            chargebackReason
            chargebackFee
            chargebackedAt
            refunds {
                id
                mollieRefundId
                amount
                reason
                status
                canceledAtProvider
                mollieCreatedAt
                adminUserId
                createDate
            }
            chargebacks {
                mollieChargebackId
                reason
                fee
                chargebackedAt
            }
        }
    }
`;

// ── Umbuchungs-Engine (#660, ADR 0006) ──────────────────────────────────────────
// Vorschau und Ausfuehrung des neuen Umbuchungs-Verfahrens. Die Dokumente stehen
// bereits hier (Teil 2c-1), damit der Codegen die Hooks erzeugt; der Tracer im
// Umbuchen-Dialog, der sie benutzt, kommt in Teil 2c-2 (#662).
//
// Keine benannten Fragmente: der Apollo-Client der Admin-App laeuft mit
// `addTypename: false`, benannte Fragmente liefern beim Cache-Read undefined-Felder.

export const TRANSFER_PLAN_PREVIEW = gql`
    query TransferPlanPreview($input: TransferPlanInput!) {
        transferPlanPreview(input: $input) {
            confirmationToken
            itemReview { sourceRateId type title amount paid open carried waived carry reason vat }
            coursePrice
            transferDate
            priceOptions { kind priceB discountB memberDiscountB lessonsTotal lessonsRemaining }
            cutoff { applicable mode lessonsTotal lessonsAttended retainedPaid carriedOpen rates { sourceRateId due consumed retainedPaid carriedOpen } }
            cutoffApplied
            oldBookingOptions { kind transferred bookingPrice open surplus firstDue }
            partialSource { cardLessons attended remaining chosen lessonPrice price }
            priceB
            discountB
            discountReason
            sourceDiscount
            sourceDiscountName
            memberDiscountB
            vatB
            rates {
                type
                amount
                due
                title
                origin
                settledByTransfer
                fullyCovered
            }
            paidA
            appliedToB
            surplus
            creditAfter
            sourceCreditedBalance
            creditReapplied
            surplusHandling { kind creditAmount refundAmount reason }
            surplusMollieRefund { possible amount remainingCredit reason payments { scheduledPaymentId molliePaymentId paidAt amount capacity title } }
            oldBooking {
                treatment
                reason
            }
            carriedOverItems {
                type
                amount
                title
            }
            hints
            blockers
            hasActivePaymentLink
            communicationDefaults {
                sendMail
                attachReversalInvoice
                attachInvoiceB
                createInvoiceBNow
                templateAvailable
            }
        }
    }
`;

export const EXECUTE_TRANSFER = gql`
    mutation ExecuteTransfer($input: ExecuteTransferInput!) {
        executeTransfer(input: $input) {
            transferOperationId
            oldBookingDeleted
            promotedBookingId
            messages
            mailSent
            warnings
            reversalInvoice { id invoiceNumber invoiceNumberPrefix }
            invoiceB { id invoiceNumber invoiceNumberPrefix }
            surplusOutcome { kind requestedRefund refunded creditRemaining mollieRefundId reason refunds { scheduledPaymentId molliePaymentId amount mollieRefundId } }
            booking {
                id
                bookingType
                bookingNumber
                bookingNumberPrefix
                grossPrice
                netPrice
                vat
                courseId
            }
            plan {
                confirmationToken
                itemReview { sourceRateId type title amount paid open carried waived carry reason vat }
                rates { type amount due title origin settledByTransfer fullyCovered }
                paidA
                carriedOverItems { type amount title }
                priceB
                discountB
                discountReason
                appliedToB
                surplus
                creditAfter
                sourceCreditedBalance
                creditReapplied
                surplusHandling { kind creditAmount refundAmount reason }
                oldBooking {
                    treatment
                    reason
                }
                hints
                blockers
            }
        }
    }
`;

// ── Bulk-Umbuchung über die Engine (#666, ADR 0006) ─────────────────────────────
// Eine TransferPlanPreview je Buchung (dieselben Felder wie die Einzel-Vorschau, ohne
// Preisoptionen/Mollie-Refund: Bulk kennt nur Kurspreis/anteilig und Guthaben) plus die
// Blocker des Laufs. Keine benannten Fragmente (addTypename: false).

export const TRANSFER_BULK_PLAN_PREVIEW = gql`
    query TransferBulkPlanPreview($input: TransferBulkPlanInput!) {
        transferBulkPlanPreview(input: $input) {
            blockers
            maxBookings
            templateAvailable
            rows {
                sourceBookingId
                sourceBookingNumber
                attendeeName
                plan {
                    confirmationToken
                    transferDate
                    priceOptions { kind priceB discountB memberDiscountB lessonsTotal lessonsRemaining }
                    cutoff { applicable mode lessonsTotal lessonsAttended retainedPaid carriedOpen rates { sourceRateId due consumed retainedPaid carriedOpen } }
            cutoffApplied
            oldBookingOptions { kind transferred bookingPrice open surplus firstDue }
            partialSource { cardLessons attended remaining chosen lessonPrice price }
                    priceB
                    discountB
                    discountReason
                    paidA
                    appliedToB
                    surplus
                    creditAfter
                    sourceCreditedBalance
                    creditReapplied
                    oldBooking { treatment reason }
                    carriedOverItems { type amount title }
                    hints
                    blockers
                    hasActivePaymentLink
                    communicationDefaults { sendMail attachReversalInvoice attachInvoiceB createInvoiceBNow templateAvailable }
                }
            }
        }
    }
`;

export const EXECUTE_BULK_TRANSFER = gql`
    mutation ExecuteBulkTransfer($input: ExecuteBulkTransferInput!) {
        executeBulkTransfer(input: $input) {
            warnings
            results {
                sourceBookingId
                sourceBookingNumber
                transferOperationId
                oldBookingDeleted
                mailSent
                warnings
                reversalInvoice { id invoiceNumber invoiceNumberPrefix }
                invoiceB { id invoiceNumber invoiceNumberPrefix }
                booking { id bookingType bookingNumber bookingNumberPrefix grossPrice courseId }
                plan { priceB appliedToB surplus creditAfter oldBooking { treatment reason } }
            }
        }
    }
`;
